import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ApiErrorResponse, ApiResponse, ApiServiceInterface } from '@hutsix/ngxh6';
import { passwordMatchValidator } from '../../../../../validators/password-match.validator';

/**
 * Accepting an invitation to an account staff created - Business Login holders, mostly.
 *
 * Separate from the password reset page because the two aren't the same thing: a reset
 * assumes you already had an account and remembers nothing about you, whereas an
 * invitation greets you by name and explains where you've been given access.
 */
@Component({
    selector: 'app-invite',
    templateUrl: './invite.component.html',
    styleUrls: ['./invite.component.scss'],
    standalone: false,
})
export class InviteComponent implements OnInit {
    public form: FormGroup;
    public token!: string;
    public firstName?: string;
    public email?: string;

    public loading = true;
    public invalid = false;
    public saving = false;
    public success = false;
    public error?: string | null;

    constructor(
        @Inject('ApiService') private api: ApiServiceInterface,
        private route: ActivatedRoute,
        private router: Router,
        private formBuilder: FormBuilder,
        private cdRef: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.form = this.formBuilder.group(
            {
                password: ['', [Validators.required, Validators.minLength(10)]],
                repeatPassword: ['', [Validators.required]],
            },
            { validator: passwordMatchValidator('password', 'repeatPassword') },
        );

        this.token = this.route.snapshot.params.token;

        // Check the link before showing the form, so a dead one fails now rather than
        // after they've chosen and typed a password twice.
        // bypassOfflineQueue: the invitee is logged out, and without it a guest GET is parked in
        // the offline queue and never sent (no XHR, endless spinner) rather than firing.
        this.api.get({ url: `/api/user/invite/${this.token}`, skipAuth: true, bypassOfflineQueue: true, useCache: false, displayErrors: false }).subscribe(
            (res: ApiResponse<{ firstName: string; email: string }>) => {
                this.firstName = res.data?.firstName;
                this.email = res.data?.email;
                this.loading = false;
                this.cdRef.detectChanges();
            },
            (_err: ApiErrorResponse<any>) => {
                this.loading = false;
                this.invalid = true;
                this.cdRef.detectChanges();
            },
        );
    }

    public submit(): void {
        if (this.form.invalid || this.saving) return;
        this.saving = true;
        this.error = null;
        this.api
            .post({
                url: `/api/user/invite/${this.token}`,
                data: { password: this.form.get('password')?.value },
                skipAuth: true,
                offline: false,
                displayErrors: false,
            })
            .subscribe(
                () => {
                    this.saving = false;
                    this.success = true;
                    this.cdRef.detectChanges();
                },
                (err: ApiErrorResponse<any>) => {
                    this.saving = false;
                    this.error = err.error?.error?.error || 'Could not set your password. Please try again.';
                    this.cdRef.detectChanges();
                },
            );
    }

    public goToLogin(): void {
        this.router.navigate(['/login']);
    }
}
